<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
    items: { type: Array, required: true },
    labelKey: { type: String, default: 'label' },
    valueKey: { type: String, default: 'value' },
    placeholder: { type: String, default: 'Choose…' },
    searchPlaceholder: { type: String, default: 'Search…' },
    loading: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false }   // <‑‑ новое свойство
});
const emit = defineEmits(['select', 'search']);
const model = defineModel();

// --------------------- реактивность ---------------------
const root = ref(null);
const open = ref(false);
const selected = ref(null);
const openState = reactive({});
const hoveredIdx = ref(null);
const searchQuery = ref('');

/* ---------- сброс открытых под‑меню ---------- */
function resetOpenState() {
    Object.keys(openState).forEach(k => delete openState[k]);
}

/* ---------- открываем/закрываем основной список ---------- */
function toggle() {
    if (!open.value) resetOpenState();   // перед открытием
    open.value = !open.value;
    if (!open.value) resetOpenState();   // перед закрытием
}

/* ---------- выбор элемента ---------- */
function onSelect(item) {
    selected.value = item;
    model.value = item[props.valueKey];
    emit('select', item[props.valueKey]);
    open.value = false;
    hoveredIdx.value = null;
    resetOpenState();
}

/* ---------- подменю открытие / закрытие ---------- */
function handleMouseEnter(idx, item) {
    if (!item.disabled) openState[idx] = true;
    hoveredIdx.value = idx;
}
function handleMouseLeave(idx, item) {
    if (!item.disabled) openState[idx] = false;
    hoveredIdx.value = null;
}

/* ---------- под‑меню: выбор ---------- */
function selectSub(sub, parentIdx) {
    onSelect(sub);
    hoveredIdx.value = parentIdx;
}

/* ---------- клик вне выпадающего списка ---------- */
function handleClickOutside(e) {
    if (root.value && !root.value.contains(e.target)) {
        open.value = false;
        resetOpenState();
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    if (model.value) {
        selected.value = findItem(model.value, props.items);
    }
});
onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside));

/* ---------- поисковый фильтр ---------- */
function onSearch() {
    emit('search', searchQuery.value);
}
const filteredItems = computed(() => {
    if (!props.searchable) return props.items;

    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return props.items;
    
    return props.items.filter(it => {
        const mainMatch = it[props.labelKey]?.toLowerCase().includes(q);
        if (mainMatch) return true;
        if (it.menu) {
            return it.menu.some(sub => sub[props.labelKey]?.toLowerCase().includes(q));
        }
        return false;
    });
});

/* ---------- сброс открытых пунктов при смене данных ---------- */
watch(
    () => props.items,
    () => Object.keys(openState).forEach(k => delete openState[k])
);

watch(model, (newVal) => {
    if (newVal) {
        selected.value = findItem(newVal, props.items);
    }
})

function findItem(id, items) {
    let findedItem = null

    for (let i = 0; i < items.length; i++) {
        if (Object.hasOwn(items[i], 'menu')) {
            findedItem = findItem(id, items[i].menu);
            if (findedItem) break;
        }

        if (!findedItem && items[i].value === id) {
            findedItem = items[i];
            break;
        }
    }

    return findedItem;
}
</script>

<template>
    <!-- Корневой элемент -->
    <div class="relative inline-block select-none w-full h-14" @click.stop>
        <!-- Кнопка‑триггер -->
        <button 
            type="button" 
            class="bg-white w-full px-4 h-14 border-2 border-orange-400 rounded-md flex justify-between items-center cursor-pointer" 
            @click="toggle" 
            :aria-expanded="open.toString()"
            aria-haspopup="true"
        >
            <span 
                class="font-sans font-normal"
                :class="`${selected?.[labelKey] ? 'text-black' : 'text-gray-400'}`"
            >{{ selected?.[labelKey] ?? placeholder }}</span>
            <span class="material-icons arrow text-orange-400">arrow_drop_down</span>
        </button>

        <!-- Основной список -->
        <ul v-if="open" class="dropdown-menu" role="menu">
            <!-- поле поиска (если разрешено) -->
            <li v-if="searchable" class="dropdown-search">
                <input type="text" v-model="searchQuery" @input="onSearch" :placeholder="searchPlaceholder" />
            </li>
            
            <!-- Если данные загружаются – показываем лоадер -->
            <li v-if="props.loading" class="loading-item">
                <div class="spinner"></div>
            </li>

            <!-- динамический список элементов (показываем только если не загружается) -->
            <li v-if="!props.loading" v-for="(item, idx) in filteredItems" :key="idx" class="dropdown-item"
                :class="{ hovered: hoveredIdx === idx }" @mouseenter="handleMouseEnter(idx, item)"
                @mouseleave="handleMouseLeave(idx, item)">
                <!-- Сам элемент -->
                <div class="item-label" :class="{ disabled: item.disabled }"
                    @click="!item.menu && !item.disabled && onSelect(item)">
                    {{ item[labelKey] }}
                    <span v-if="item.menu" class="material-icons caret" aria-hidden="true">
                        chevron_right
                    </span>
                </div>

                <!-- Вложенный список (если есть) -->
                <ul v-show="item.menu && openState[idx]" class="submenu" role="menu">
                    <li v-for="(sub, subIdx) in item.menu" :key="subIdx" :class="{ disabledItem: sub.disabled }"
                        @click.stop="!sub.disabled && selectSub(sub, idx)">
                        {{ sub[labelKey] }}
                    </li>
                </ul>
            </li>
        </ul>
    </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

/* ---------- Основные стили ---------- */
.nested-dropdown {
    /* position: relative;
    display: inline-block;
    user-select: none; */
    width: 100%; 
    max-width: 360px;
    height: 50px;
}

.dropdown-toggle {
    /* padding: 8px 12px;
    font-size: 1rem;
    cursor: pointer;
    border: 2px solid #FF8818;
    border-radius: 0.5rem;
    background: #fff;
    text-align: left;
    min-width: 0; 
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center; */
}

.arrow {
    /* font-size: 18px; */
}

.dropdown-menu,
.submenu {
    position: absolute;
    top: 100%;
    left: 0;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    background: #fff;
    border: 2px solid #FF8818;
    border-radius: 0.5rem;
    min-width: 0; 
    width: 100%;
    z-index: 10;
}

.submenu {
    left: 100%;
    top: 0;
    min-width: 160px;
    z-index: 2000;
}

/* --- поиск --- */
.dropdown-search {
    padding: 4px 8px;
}

.dropdown-search input {
    width: 100%;
    padding: 4px;
    border: 1px solid #ccc;
    border-radius: 0.3rem;
    font-size: 0.9rem;
}

/* ---------- пункты ---------- */
.dropdown-item {
    position: relative;
}

.item-label {
    padding: 6px 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

/* --- под‑меню стили (то же, что и .item-label) --- */
.submenu li {
    padding: 6px 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

/* --- hover для обычных пунктов --- */
.item-label:hover,
.submenu li:hover,
.submenu li:focus {
    background: #7DD3FC;
    color: #ffffff;
}

/* --- disabled у главных пунктов (не меняем стиль) --- */
.item-label.disabled {
    cursor: default;
    color: inherit;
}

/* --- disabled у под‑меню --- */
.submenu li.disabledItem {
    color: #777;
    font-style: italic;
    cursor: default;
}

.submenu li.disabledItem:hover {
    background: none;
    color: #777;
    cursor: default;
}

/* --- подсветка родителя после выбора из под‑меню --- */
.dropdown-item.hovered>.item-label {
    background: #7DD3FC;
    color: #ffffff;
}

.caret { font-size: 18px; }

@media (max-width: 768px) {


  /* подчёркиваем подсказку, чтобы было ясно, что меню открыто */
  .dropdown-item:not(.disabled) .item-label:hover,
  .active-menu .item-label        { background: #e3f2fd; }

  /* подменю «упаковывается» прямо под родителем */
  .submenu {
    left: 0;
    top: 100%;
    /* чтобы подсказка не прятала кнопку, ставим z-index */
    z-index: 2000;
  }

  /* убираем стрелочку‑caret (опционально) – удобно на телефоне */
  .caret { display: none; }
}
</style>
